// Mid-call device switching (F-CALL-3): hot-swap mic/cam/speaker; choices
// persist under bz-device-prefs via the engine's DeviceManager.

import { ChangeEvent, ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import { engine } from '../engine';
import { pushToast, useCallStore } from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import {
  IconCamera,
  IconChevronDown,
  IconClose,
  IconDevices,
  IconMicrophone,
  IconProps,
} from '../ui/icons';

const KINDS: { kind: MediaDeviceKind; label: string; Icon: ComponentType<IconProps> }[] = [
  { kind: 'audioinput', label: 'Microphone', Icon: IconMicrophone },
  { kind: 'videoinput', label: 'Camera', Icon: IconCamera },
  { kind: 'audiooutput', label: 'Speaker', Icon: IconDevices },
];

export function DeviceMenu({ onClose, kinds }: { onClose: () => void; kinds?: MediaDeviceKind[] }) {
  const [devices, setDevices] = useState<Record<string, MediaDeviceInfo[]>>({});
  const [active, setActive] = useState<Record<string, string | undefined>>({});
  // Re-enumerate when the hardware set changes (unplug/replug).
  const devicesEpoch = useCallStore((s) => s.devicesEpoch);
  // Which rows to show: the mic caret passes [mic, speaker], the camera caret
  // [camera], the ⋮ entry passes nothing → all three.
  const shown = useMemo(() => (kinds ? KINDS.filter((k) => kinds.includes(k.kind)) : KINDS), [kinds]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const byKind: Record<string, MediaDeviceInfo[]> = {};
      const act: Record<string, string | undefined> = {};
      for (const { kind } of shown) {
        byKind[kind] = await engine.devices.list(kind);
        act[kind] = engine.activeDeviceId(kind);
      }
      if (!cancelled) {
        setDevices(byKind);
        setActive(act);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [devicesEpoch, shown]);

  const onPick = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const kind = e.currentTarget.dataset.kind as MediaDeviceKind;
    const deviceId = e.currentTarget.value;
    engine
      .switchDevice(kind, deviceId)
      .then(() => setActive((a) => ({ ...a, [kind]: deviceId })))
      .catch((err: unknown) => pushToast(err instanceof Error ? err.message : String(err), 'error'));
  }, []);

  return (
    <div className="bz-device-menu" role="dialog" aria-label="Device settings">
      <div className="bz-device-grid">
        {shown.map(({ kind, label, Icon }) => (
          <div key={kind} className="bz-device-select">
            <span className="bz-device-select-icon">
              <Icon size={16} />
            </span>
            <select aria-label={label} value={active[kind] ?? ''} data-kind={kind} onChange={onPick}>
              {(devices[kind] ?? []).map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `${label} ${d.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
            <IconChevronDown size={15} className="bz-device-chev" />
          </div>
        ))}
        <IconButton shape="round" label="Close" onClick={onClose} className="bz-device-close">
          <IconClose size={16} />
        </IconButton>
      </div>
    </div>
  );
}
